// slices/fundManagementInvestment/fundManagementReportsSlice.ts
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';

// Expense ledger entry interface - Updated to match API response
export interface UserDetails {
  id?: string;
  username?: string;
  gender?: string;
  phone?: string;
  dob?: string;
  surburb?: string;
  state?: string;
  country_res?: string;
  postcode?: string;
  address?: string;
  groups?: string[];
  avatar?: string;
  user_details?: {
    name?: string;
    role?: string[];
    email?: string;
  };
}

export interface ExpenseLedgerEntry {
  expenseId: string;
  id?: string;
  groupId?: string;
  serviceId?: string;
  unitType?: 'incomeUnits' | 'growthUnits';
  type: string; 
  expenseType?: string; 
  amount: number | string;
  date: string;
  description?: string;
  status?: string;
  relatedAssetId?: string;
  relatedRequestId?: string;
  relatedInvestorEmail?: string;
  investorName?: string;
  units?: string | number;
  unitPrice?: string | number;
  operationCharge?: string | number;
  isOperationChargeEntry?: boolean;
  chargeEntryOf?: string;
  createdBy?: string;
  createdDate?: string;
  updatedBy?: string;
  updatedDate?: string;
  lastUpdatedDateTime?: string;
  dateFolder?: string;
  userDetails?: UserDetails;
}

type ExpenseLedgerEntriesByType = {
  [key: string]: ExpenseLedgerEntry[];
};

type ExpenseLedgerEntriesData = ExpenseLedgerEntry[] | ExpenseLedgerEntriesByType;

// Payload interface for getting expense ledger entries
export interface GetExpenseLedgerEntriesPayload {
  mode: 'admin' | 'investor';
  groupId?: string;
  serviceId?: string;
  unitType?: 'incomeUnits' | 'growthUnits';
  pageSize: number;
  continuationToken?: string | null;
  pageNumber?: number;
}

// Response interface for expense ledger entries - UPDATED to match actual API response
export interface ExpenseLedgerEntriesResponse {
  expenseLedgerEntries: ExpenseLedgerEntriesData;
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
  nextToken: string | null;
  hasMore: boolean;
  filters?: any;
}

// State interface
export interface FundManagementReportsState {
  // Expense ledger entries state
  expenseLedgerLoading: boolean;
  expenseLedgerError: string | null;
  expenseLedgerEntries: ExpenseLedgerEntriesData;
  expenseLedgerContinuationToken: string | null;
  expenseLedgerHasMore: boolean;
  expenseLedgerTotalCount: number;
}

const createEmptyLedgerResponse = (): ExpenseLedgerEntriesResponse => ({
  expenseLedgerEntries: {},
  totalCount: 0,
  pageSize: 0,
  currentPage: 1,
  totalPages: 0,
  nextToken: null,
  hasMore: false
});

const normalizeLedgerResponse = (data: any): ExpenseLedgerEntriesResponse => {
  if (!data || typeof data !== 'object') {
    return createEmptyLedgerResponse();
  }

  // For investor-ledger API response (pagination structure)
  if ('totalCount' in data && 'currentPage' in data && 'pageSize' in data) {
    let expenseEntries = data.expenseLedgerEntries || {};
    
    // Normalize entries - apply date field normalization
    if (typeof expenseEntries === 'object' && !Array.isArray(expenseEntries)) {
      const normalizedGrouped: any = {};
      for (const [key, entries] of Object.entries(expenseEntries)) {
        if (Array.isArray(entries)) {
          normalizedGrouped[key] = normalizeEntriesList(entries);
        }
      }
      expenseEntries = normalizedGrouped;
    } else if (Array.isArray(expenseEntries)) {
      expenseEntries = normalizeEntriesList(expenseEntries);
    }
    
    return {
      expenseLedgerEntries: expenseEntries,
      totalCount: data.totalCount || 0,
      pageSize: data.pageSize || 30,
      currentPage: data.currentPage || 1,
      totalPages: data.totalPages || Math.ceil((data.totalCount || 0) / (data.pageSize || 1)),
      nextToken: data.nextToken || null,
      hasMore: data.hasMore !== undefined ? data.hasMore : false,
      filters: data.filters
    };
  }

  // For admin expense ledger (continuation token structure)
  if ('expenseLedgerEntries' in data) {
    let expenseEntries = data.expenseLedgerEntries || {};
    
    // Normalize entries
    if (typeof expenseEntries === 'object' && !Array.isArray(expenseEntries)) {
      const normalizedGrouped: any = {};
      for (const [key, entries] of Object.entries(expenseEntries)) {
        if (Array.isArray(entries)) {
          normalizedGrouped[key] = normalizeEntriesList(entries);
        }
      }
      expenseEntries = normalizedGrouped;
    }
    
    return {
      ...createEmptyLedgerResponse(),
      ...data,
      expenseLedgerEntries: expenseEntries
    };
  }

  // If response is already grouped by transaction type
  const groupedValues = Object.values(data) as unknown[];
  const looksLikeGroupedLedger = groupedValues.every((value) => Array.isArray(value));

  if (looksLikeGroupedLedger) {
    const totalCount = groupedValues.reduce<number>(
      (count, value) => count + (Array.isArray(value) ? value.length : 0),
      0
    );

    // Normalize all entries
    const normalizedGrouped: any = {};
    for (const [key, entries] of Object.entries(data)) {
      if (Array.isArray(entries)) {
        normalizedGrouped[key] = normalizeEntriesList(entries);
      }
    }

    return {
      ...createEmptyLedgerResponse(),
      expenseLedgerEntries: normalizedGrouped,
      totalCount
    };
  }

  return createEmptyLedgerResponse();
};

const flattenEntries = (entries: ExpenseLedgerEntriesData): ExpenseLedgerEntry[] => {
  if (Array.isArray(entries)) {
    return entries;
  }

  if (entries && typeof entries === 'object') {
    return Object.values(entries).flat().filter(Boolean);
  }

  return [];
};

const UNIT_TRANSACTION_TYPES = new Set([
  'incomeContribution',
  'growthContribution',
  'incomeUnitSell',
  'growthUnitSell',
  'incomeAssetPurchase',
  'growthAssetPurchase',
  'incomeAssetSell',
  'growthAssetSell',
  'incomeRevaluation',
  'growthRevaluation',
  'cashUsed'
]);

const isSyntheticChargeEntry = (entry: ExpenseLedgerEntry): boolean => {
  return Boolean(entry.isOperationChargeEntry || entry.chargeEntryOf || entry.expenseId?.endsWith('-operation-charge'));
};

const getChargeParentId = (entry: ExpenseLedgerEntry): string | null => {
  if (entry.chargeEntryOf) return entry.chargeEntryOf;
  if (entry.expenseId?.endsWith('-operation-charge')) {
    return entry.expenseId.replace(/-operation-charge$/, '');
  }
  return null;
};

const parsePositiveAmount = (value: string | number | undefined): number => {
  if (value === undefined || value === null || value === '') return 0;
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const buildChargeDescription = (entry: ExpenseLedgerEntry): string => {
  const investorLabel = entry.investorName || entry.relatedInvestorEmail || entry.userDetails?.user_details?.name || 'Investor';
  if (entry.type === 'incomeUnitSell' || entry.type === 'growthUnitSell') {
    return `Unit sell: Operation Charge:(${investorLabel})`;
  }
  if (entry.type === 'incomeAssetPurchase' || entry.type === 'growthAssetPurchase') {
    return `Asset purchase: Operation Charge:(${investorLabel})`;
  }
  if (entry.type === 'incomeAssetSell' || entry.type === 'growthAssetSell') {
    return `Asset sell: Operation Charge:(${investorLabel})`;
  }
  if (entry.type === 'incomeRevaluation' || entry.type === 'growthRevaluation') {
    return `Revaluation: Operation Charge:(${investorLabel})`;
  }
  if (entry.type === 'cashUsed') {
    return `Operational Expense: Operation Charge:(${investorLabel})`;
  }
  return `Unit contribution: Operation Charge:(${investorLabel})`;
};

const cleanMainDescription = (entry: ExpenseLedgerEntry): string | undefined => {
  const description = entry.description;
  if (!description) return description;

  return description
    .replace(/,?\s*Operation\s*charge\s*:\s*[^,]+/i, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+,/g, ',')
    .trim();
};

const splitOperationChargeEntries = (entries: ExpenseLedgerEntry[]): ExpenseLedgerEntry[] => {
  const existingChargeParents = new Set(
    entries
      .map((entry) => getChargeParentId(entry))
      .filter((value): value is string => Boolean(value))
  );

  return entries.flatMap((entry) => {
    const normalizedEntry = normalizeDateFields(entry);

    if (isSyntheticChargeEntry(normalizedEntry)) {
      return [normalizedEntry];
    }

    if (!UNIT_TRANSACTION_TYPES.has(normalizedEntry.type)) {
      return [normalizedEntry];
    }

    const cleanedMainEntry: ExpenseLedgerEntry = {
      ...normalizedEntry,
      description: cleanMainDescription(normalizedEntry)
    };

    const operationChargeAmount = parsePositiveAmount(normalizedEntry.operationCharge);
    if (existingChargeParents.has(normalizedEntry.expenseId)) {
      return [cleanedMainEntry];
    }

    const chargeEntry: ExpenseLedgerEntry = {
      ...normalizedEntry,
      expenseId: `${normalizedEntry.expenseId}-operation-charge`,
      amount: String(operationChargeAmount),
      description: buildChargeDescription(normalizedEntry),
      operationCharge: undefined,
      isOperationChargeEntry: true,
      chargeEntryOf: normalizedEntry.expenseId
    };

    return [
      {
        ...cleanedMainEntry,
        operationCharge: undefined
      },
      chargeEntry
    ];
  });
};

// Normalize date fields from API response
const normalizeDateFields = (entry: ExpenseLedgerEntry): ExpenseLedgerEntry => {
  // Function to reconstruct potentially incomplete dates
  const normalizeDate = (dateVal: any): string => {
    if (!dateVal) return '';
    
    const dateStr = String(dateVal).trim();
    
    // Check if it's a time-only string (e.g., "05:30:00" or "5:30")
    if (/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.test(dateStr)) {
      console.warn('Detected time-only string in API response:', dateStr, 'in entry:', entry.expenseId);
      
      // Try to find date info in the description field
      if (entry.description && typeof entry.description === 'string') {
        // Look for date patterns in description like "04 Apr" or "2026-04-09"
        const datePattern = entry.description.match(/(\d{1,2}\s+\w+\s+\d{4})|(\d{4}-\d{2}-\d{2})/);
        if (datePattern) {
          console.log('Found potential date in description:', datePattern[0]);
          // Try to combine the date from description with the time
          try {
            const potentialDate = new Date(`${datePattern[0]} ${dateStr}`);
            if (!isNaN(potentialDate.getTime())) {
              return potentialDate.toISOString();
            }
          } catch (e) {
            // Fall through to return as-is
          }
        }
      }
    }
    
    return dateStr;
  };

  return {
    ...entry,
    // Ensure date field is a proper ISO string or timestamp
    date: normalizeDate(entry.date) || entry.date,
    // Also normalize other date fields
    createdDate: normalizeDate(entry.createdDate) || entry.createdDate,
    updatedDate: normalizeDate(entry.updatedDate) || entry.updatedDate,
    lastUpdatedDateTime: normalizeDate(entry.lastUpdatedDateTime) || entry.lastUpdatedDateTime
  };
};

// Deep normalize entries list
const normalizeEntriesList = (entries: ExpenseLedgerEntry[]): ExpenseLedgerEntry[] => {
  return splitOperationChargeEntries(entries);
};

// Async thunk for getting expense ledger entries
export const getExpenseLedgerEntries = createAsyncThunk(
  'fundManagementReports/getExpenseLedgerEntries',
  async (payload: GetExpenseLedgerEntriesPayload, { rejectWithValue }) => {
    try {
      const isInvestorMode = payload.mode === 'investor';

      const response = isInvestorMode
        ? await axiosInstance.get('/fund-investment-management/investor-ledger', {
            params: {
              page_size: payload.pageSize,
              page_number: payload.pageNumber || 1
            }
          })
        : await axiosInstance.post('/fund-investment-management/get_expense_ledger_entries', {
            groupId: payload.groupId,
            serviceId: payload.serviceId,
            unitType: payload.unitType,
            pageSize: payload.pageSize,
            continuationToken: payload.continuationToken || null
          });

      // Debug: Log raw API response to verify backend timestamp normalization
      console.log('=== EXPENSE LEDGER API RESPONSE ===');
      console.log('Mode:', isInvestorMode ? 'Investor' : 'Admin');
      console.log('Raw Response:', response.data);
      
      // Check if backend normalization worked
      const sampleEntries: any[] = [];
      if (Array.isArray(response.data.expenseLedgerEntries)) {
        sampleEntries.push(...response.data.expenseLedgerEntries.slice(0, 3));
      } else if (response.data.expenseLedgerEntries) {
        Object.values(response.data.expenseLedgerEntries).forEach((entries: any) => {
          if (Array.isArray(entries)) sampleEntries.push(...entries.slice(0, 3));
        });
      }
      
      if (sampleEntries.length > 0) {
        console.group('📊 BACKEND NORMALIZATION CHECK');
        sampleEntries.forEach((entry, idx) => {
          const hasNormalizedTime = entry.date && !entry.date.includes('T00:00:00');
          const status = hasNormalizedTime ? '✅ NORMALIZED' : '⚠️ MIDNIGHT';
          console.log(`Entry ${idx}:`, {
            status,
            date: entry.date,
            lastUpdatedDateTime: entry.lastUpdatedDateTime,
            createdDateTime: entry.createdDateTime
          });
        });
        console.groupEnd();
      }

      return normalizeLedgerResponse(response.data);
    } catch (error: any) {
      if (error.response?.status === 404 || error.response?.status === 204) {
        return createEmptyLedgerResponse();
      }

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch expense ledger entries';

      if (typeof errorMessage === 'string' && /no data|not found|no entries/i.test(errorMessage)) {
        return createEmptyLedgerResponse();
      }

      return rejectWithValue(
        errorMessage
      );
    }
  }
);

const initialState: FundManagementReportsState = {
  expenseLedgerLoading: false,
  expenseLedgerError: null,
  expenseLedgerEntries: {},
  expenseLedgerContinuationToken: null,
  expenseLedgerHasMore: false,
  expenseLedgerTotalCount: 0
};

const fundManagementReportsSlice = createSlice({
  name: 'fundManagementReports',
  initialState,
  reducers: {
    // Clear expense ledger data
    clearExpenseLedgerEntries: (state) => {
      state.expenseLedgerEntries = {};
      state.expenseLedgerContinuationToken = null;
      state.expenseLedgerHasMore = false;
      state.expenseLedgerError = null;
      state.expenseLedgerLoading = false;
      state.expenseLedgerTotalCount = 0;
    },
    // Reset pagination for new search
    resetExpenseLedgerPagination: (state) => {
      state.expenseLedgerContinuationToken = null;
      state.expenseLedgerHasMore = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get expense ledger entries
      .addCase(getExpenseLedgerEntries.pending, (state) => {
        state.expenseLedgerLoading = true;
        state.expenseLedgerError = null;
      })
      .addCase(getExpenseLedgerEntries.fulfilled, (state, action) => {
        state.expenseLedgerLoading = false;

        // Check if action.payload exists and has expenseLedgerEntries property
        if (!action.payload || !action.payload.expenseLedgerEntries) {
          state.expenseLedgerEntries = {};
          state.expenseLedgerContinuationToken = null;
          state.expenseLedgerHasMore = false;
          return;
        }

        const isInvestorMode = action.meta.arg.mode === 'investor';
        const wasInitialFetch = isInvestorMode
          ? (action.meta.arg.pageNumber || 1) <= 1
          : action.meta.arg.continuationToken === null;

        if (wasInitialFetch) {
          state.expenseLedgerEntries = action.payload.expenseLedgerEntries || {};
        } else {
          const existingEntries = flattenEntries(state.expenseLedgerEntries);
          const incomingEntries = flattenEntries(action.payload.expenseLedgerEntries || {});
          state.expenseLedgerEntries = [...existingEntries, ...incomingEntries];
        }

        state.expenseLedgerContinuationToken = action.payload.nextToken || null;
        state.expenseLedgerHasMore = action.payload.hasMore || false;
        state.expenseLedgerTotalCount = action.payload.totalCount || 0;
      })
      .addCase(getExpenseLedgerEntries.rejected, (state, action) => {
        state.expenseLedgerLoading = false;
        state.expenseLedgerError = action.payload as string;
      });
  }
});

export const { clearExpenseLedgerEntries, resetExpenseLedgerPagination } =
  fundManagementReportsSlice.actions;

export default fundManagementReportsSlice.reducer;
